import {
    EVENT_ATTRIBUTE_CHANGED, EVENT_ITEM_CHANGED, EVENT_LOOP_UPDATED,
    EVENT_PATH_UPDATED, EVENT_FLOW_CHANGED, EVENT_VALIDATION_CHANGED,
    EVENT_ERROR_RAISED,
} from './events';
import { buildPath, checkCompleteLoop } from './utils';
import { seedCurrentItemState, resolveNextItem, applyDeprecatedOptionsCleanup, persistLoopIterationAnswer, syncNextItemState, reconcileLoopEntry } from './transitions';

// Deep clone used by the read-only traverse() — all flow state is JSON-serialisable
// (it round-trips through persistence), so this is sufficient and avoids pulling
// in lodash just for cloneDeep.
function cloneDeepState(value) {
    if (value === undefined || value === null) return value;
    return JSON.parse(JSON.stringify(value));
}

export class FlowEngine {
    constructor({ flowRegistry, flowId, initialState }) {
        if (!flowRegistry || !flowRegistry.has(flowId)) {
            throw new Error(`FlowEngine: flow '${flowId}' is not registered`);
        }
        this.flowRegistry = flowRegistry;
        this._items = {};
        const flow = flowRegistry.get(flowId);
        flow.items.forEach((item) => { this._items[item.id] = item; });

        if (initialState) {
            this._snapshot = { ...initialState };
            const currentItem = this._items[this._snapshot.currentItemId];
            if (currentItem) {
                currentItem.init(
                    this._snapshot.itemsStates,
                    this._snapshot.globals,
                    this._snapshot.loops
                );
            }
        } else {
            const firstItem = flow.items[0];
            firstItem.init({}, {}, {});
            const itemsStates = {};
            if (firstItem.defaultValues) {
                itemsStates[firstItem.id] = { values: firstItem.defaultValues };
            }
            this._snapshot = {
                currentFlowId: flow.id,
                currentFlowVersion: flow.version,
                currentFlowName: flow.name,
                currentItemId: firstItem.id,
                itemsStates,
                loops: {},
                globals: {},
                path: [{ itemId: firstItem.id, loop: null, group: firstItem.group }],
                hasErrors: false,
                hasIncompleteLoop: false,
                canFreelyNavigate: true,
            };
        }
    }

    getSnapshot() { return this._snapshot; }

    _getItemsMap() { return this._items; }

    getCurrentItem() {
        return this._items[this._snapshot.currentItemId];
    }

    getCurrentFields() {
        return this.getCurrentItem().fields || [];
    }

    getCurrentValues() {
        const id = this._snapshot.currentItemId;
        return this._snapshot.itemsStates[id]?.values;
    }

    getPath() { return this._snapshot.path; }

    getLoopSnapshot(loopName) { return this._snapshot.loops[loopName]; }

    // `options.loopName` / `options.loopIndex` target a specific loop iteration
    // instead of the loop's current index. The continuous-flow view needs this:
    // it renders every iteration at once, so an edit may land on an iteration
    // other than the one the engine is currently positioned on. Omit them and
    // the write goes to the current iteration, as it always has.
    setAttribute(itemId, attribute, fieldIndex, value, options = {}) {
        const item = this._items[itemId];
        const fieldCount = item.fields ? item.fields.length : 0;
        const snap = this._snapshot;
        const loopName = options.loopName !== undefined ? options.loopName : item.loopName;
        const loopRec = loopName ? snap.loops[loopName] : undefined;
        const targetIndex = loopRec
            ? (options.loopIndex !== undefined ? options.loopIndex : loopRec.index)
            : undefined;
        // Editing a past iteration must not overwrite the current iteration's
        // mirror in itemsStates (branching/validation read it for the current item).
        const isCurrentIteration = !loopRec || targetIndex === loopRec.index;

        let currentState = snap.itemsStates[itemId];
        if (loopRec) {
            currentState = (loopRec.states[targetIndex - 1] || {})[itemId] || {};
        }

        let newAttrValue;
        if (attribute === 'applyToAll') {
            newAttrValue = value;
        } else {
            const prev = currentState?.[attribute];
            if (prev === undefined) {
                newAttrValue = Array(fieldCount);
                newAttrValue[fieldIndex] = value;
            } else {
                newAttrValue = [...prev];
                newAttrValue[fieldIndex] = value;
            }
        }

        const newItemState = { ...currentState, [attribute]: newAttrValue };
        // `fields` is the baseline fieldsChanged() compares against, normally stamped
        // by seedCurrentItemState when the engine arrives at an item. Continuous flow
        // lets an item be answered *before* the engine gets there; without a baseline
        // fieldsChanged() reports "changed" on arrival and advance() discards the
        // answer. Establish it on first write instead.
        if (newItemState.fields === undefined && item.fields !== undefined) {
            newItemState.fields = item.fields;
        }
        // The edited view is always used for validation so the item sees its own
        // new answer; it is only persisted into itemsStates for the current iteration.
        const editedStates = { ...snap.itemsStates, [itemId]: newItemState };
        const newItemsStates = isCurrentIteration ? editedStates : snap.itemsStates;
        const newLoops = { ...snap.loops };

        if (loopRec) {
            const newStates = [...loopRec.states];
            newStates[targetIndex - 1] = {
                ...newStates[targetIndex - 1],
                [itemId]: newItemState,
            };
            let newValidIterations = loopRec.validIterations;
            if (attribute !== 'applyToAll') {
                newValidIterations = [...loopRec.validIterations];
                newValidIterations[targetIndex - 1] = false;
            }
            newLoops[loopName] = {
                ...loopRec,
                states: newStates,
                validIterations: newValidIterations,
            };
        }

        const events = [{
            type: EVENT_ATTRIBUTE_CHANGED,
            itemId, attribute, fieldIndex, value, loopName,
            loopIndex: targetIndex,
        }];

        let hasErrors = snap.hasErrors;
        if (attribute === 'values' && !options.skipValidation) {
            item.validate(editedStates, snap.globals, newLoops);
            // `hasErrors` gates advance() for the *current* item, so only an edit
            // on the current iteration may change it.
            if (isCurrentIteration) {
                hasErrors = item.errors.length > 0;
                events.push({ type: EVENT_VALIDATION_CHANGED, hasErrors });
            }
        }

        this._snapshot = {
            ...snap,
            itemsStates: newItemsStates,
            loops: newLoops,
            canFreelyNavigate: false,
            hasErrors,
        };
        return { snapshot: this._snapshot, events };
    }

    setApplyToAll(itemId, value) {
        return this.setAttribute(itemId, 'applyToAll', 0, value);
    }

    advance() {
        const snap = this._snapshot;
        const currentItemId = snap.currentItemId;
        const currentItem = this._items[currentItemId];
        const events = [];

        let itemsStates = seedCurrentItemState(snap.itemsStates, currentItem);

        let nextItem = { isDecisionNode: true, errors: [] };
        let nextItemId = currentItemId;
        const globals = snap.globals;
        let loops = snap.loops;  // loop branches added in Task 8
        let hasErrors = false;

        let previousLoopName = currentItem.loopName;
        let previousLoopIndex = previousLoopName ? snap.loops[previousLoopName]?.index : undefined;
        let previousItemId = currentItemId;

        while (nextItem.isDecisionNode && !(nextItem.errors || []).length) {
            try {
                nextItem = resolveNextItem(this._items, this._items[nextItemId], itemsStates, globals, loops);
            } catch (err) {
                return {
                    snapshot: this._snapshot,
                    events: [{ type: 'errorRaised', message: err.message, stack: err.stack }],
                };
            }
            loops = persistLoopIterationAnswer(loops, previousLoopName, previousLoopIndex, previousItemId, itemsStates, nextItem.loopName);
            if (nextItem.loopName !== undefined) {
                const result = reconcileLoopEntry(loops, itemsStates, globals, nextItem, previousLoopName);
                loops = result.loops;
                itemsStates = result.itemsStates;
                if (result.hasErrors) hasErrors = true;
                events.push({ type: 'loopUpdated', loopName: nextItem.loopName, loop: loops[nextItem.loopName] });
            }
            {
                const result = syncNextItemState(itemsStates, globals, loops, nextItem);
                itemsStates = result.itemsStates;
                if (result.hasErrors !== undefined) hasErrors = result.hasErrors;
            }
            previousLoopName = nextItem.loopName;
            if (previousLoopName) previousLoopIndex = loops[previousLoopName].index;
            previousItemId = nextItem.id;
            nextItemId = nextItem.id;
        }

        let hasIncompleteLoop = false;
        const originalLoopName = currentItem.loopName;
        if (originalLoopName && !nextItem.loopName) {
            const result = checkCompleteLoop(originalLoopName, loops);
            hasIncompleteLoop = result.hasIncomplete;
            loops = result.loops;
            if (hasIncompleteLoop) {
                nextItemId = currentItemId;
            }
        }

        itemsStates = applyDeprecatedOptionsCleanup(itemsStates, this._items[nextItemId], nextItemId);

        const newPath = buildPath(
            snap, currentItemId, currentItem.group,
            nextItemId, nextItem.loopName, nextItem.group,
        );

        events.push({ type: EVENT_ITEM_CHANGED, itemId: nextItemId, path: newPath });
        events.push({ type: EVENT_PATH_UPDATED, path: newPath });

        this._snapshot = {
            ...snap,
            currentItemId: nextItemId,
            itemsStates,
            loops,
            path: newPath,
            canFreelyNavigate: true,
            hasErrors,
            hasIncompleteLoop,
        };
        return { snapshot: this._snapshot, events };
    }

    // Which item would advance() land on, without committing anything: runs the
    // real advance() on a throwaway engine over a copy of this snapshot, so the
    // answer can never drift from the transition logic it is predicting.
    //
    // Continuous mode uses this to decide whether it may follow the reader forward
    // on its own. `traverse()` cannot answer that question inside a loop: the next
    // question of an iteration has no stored answer yet, which is exactly where the
    // walk stops. Item definitions are shared singletons, so the probe's init() /
    // validate() calls do touch them — harmless, because every consumer re-inits
    // from the entry point on each tick, and the caller advances for real right
    // after a non-terminal answer.
    peekNextItemId() {
        try {
            const probe = new FlowEngine({
                flowRegistry: this.flowRegistry,
                flowId: this._snapshot.currentFlowId,
                initialState: cloneDeepState(this._snapshot),
            });
            return probe.advance().snapshot.currentItemId;
        } catch (err) {
            return undefined;
        }
    }

    // Read-only walk through the whole flow, replaying stored answers to
    // produce a linear {item, values, preliminaries, loopIndex} sequence. The
    // snapshot is NOT mutated. Used by the Summary view.
    //
    // Semantics match the former layout/utils buildSummary helper:
    // - Decision nodes are skipped except the first item.
    // - On first visit to a loop (or when re-entering from a parent),
    //   iteration resets to 1.
    // - When landing on a loop's firstItemId while already tracking that
    //   loop, the replay advances to the next stored iteration.
    // - If a non-decision item has no stored answer, the walk stops.
    // - The fields stored alongside the answer (if any) override the freshly
    //   rebuilt fields so the Summary reflects how the question was phrased
    //   when the user answered it.
    traverse() {
        const snap = this._snapshot;
        const items = this._items;
        const itemsStates = cloneDeepState(snap.itemsStates);
        const loops = cloneDeepState(snap.loops);
        const globals = cloneDeepState(snap.globals);

        const firstItem = items[snap.path && snap.path[0] ? snap.path[0].itemId : undefined]
            || Object.values(items).find((i) => !i.isDecisionNode) // fallback (unused in practice)
            || null;
        if (!firstItem) return [];

        try { firstItem.init(itemsStates, globals, loops); } catch (err) { return []; }

        const out = [];
        let current = firstItem;
        let initialState = itemsStates[firstItem.id];
        let loopIndex = firstItem.loopName && loops[firstItem.loopName]
            ? loops[firstItem.loopName].index
            : undefined;
        // `loopName` is captured per entry: item definitions are shared singletons
        // whose `loopName` is reassigned by later init() calls in this same walk,
        // so reading it off the item afterwards can report the wrong loop.
        out.push({
            item: current,
            values: initialState?.values,
            preliminaries: initialState?.preliminaries,
            loopIndex,
            loopName: firstItem.loopName,
        });

        let previousLoopName = firstItem.loopName;
        let previousLoopIndex = loopIndex || 0;
        const loopsSeen = new Set();

        while (current && current.id !== 'theEnd') {
            let nextItem;
            try {
                nextItem = resolveNextItem(items, items[current.id], itemsStates, globals, loops);
                if (!nextItem) break;
            } catch (err) {
                break;
            }
            let nextState = itemsStates[nextItem.id];
            if (nextItem.loopName && loops[nextItem.loopName]) {
                const loop = loops[nextItem.loopName];
                if (nextItem.loopName !== previousLoopName
                    && (!loopsSeen.has(nextItem.loopName)
                        || nextItem.loopName.startsWith(previousLoopName))) {
                    previousLoopIndex = 1;
                    loops[nextItem.loopName].index = 1;
                } else if (loop.firstItemId === nextItem.id) {
                    previousLoopIndex += 1;
                    loops[nextItem.loopName].index = previousLoopIndex;
                } else {
                    previousLoopIndex = loops[nextItem.loopName].index;
                }
                nextState = loops[nextItem.loopName].states[previousLoopIndex - 1]
                    && loops[nextItem.loopName].states[previousLoopIndex - 1][nextItem.id];
                itemsStates[nextItem.id] = nextState;
                if (nextState === undefined && !nextItem.isDecisionNode) {
                    break;
                }
                if (!nextItem.isDecisionNode && nextState && nextState.fields) {
                    // Replace the rebuilt fields with the snapshot taken at
                    // answer time so labels/options reflect the answered state.
                    nextItem = { ...nextItem, fields: nextState.fields };
                }
                loopsSeen.add(nextItem.loopName);
            }

            if (!nextItem.isDecisionNode) {
                out.push({
                    item: nextItem,
                    values: nextState?.values,
                    preliminaries: nextState?.preliminaries,
                    loopIndex: previousLoopIndex || undefined,
                    loopName: nextItem.loopName,
                });
            }
            previousLoopName = nextItem.loopName;
            current = nextItem;
        }
        return out;
    }

    changeFlow(flowId) {
        if (!this.flowRegistry.has(flowId)) {
            throw new Error(`FlowEngine: flow '${flowId}' is not registered`);
        }
        const flow = this.flowRegistry.get(flowId);
        this._items = {};
        flow.items.forEach((item) => { this._items[item.id] = item; });
        const firstItem = flow.items[0];
        firstItem.init({}, {}, {});
        const itemsStates = {};
        if (firstItem.defaultValues) {
            itemsStates[firstItem.id] = { values: firstItem.defaultValues };
        }
        this._snapshot = {
            currentFlowId: flow.id,
            currentFlowVersion: flow.version,
            currentFlowName: flow.name,
            currentItemId: firstItem.id,
            itemsStates,
            loops: {},
            globals: {},
            path: [{ itemId: firstItem.id, loop: null, group: firstItem.group }],
            hasErrors: false,
            hasIncompleteLoop: false,
            canFreelyNavigate: true,
        };
        return {
            snapshot: this._snapshot,
            events: [{ type: EVENT_FLOW_CHANGED, flowId }],
        };
    }

    setLoopPreliminary(loopName) {
        const snap = this._snapshot;
        const flow = this.flowRegistry.get(snap.currentFlowId);
        const def = flow.loopsDefinitions?.[loopName];
        if (!def?.allowPreliminary) return { snapshot: snap, events: [] };
        const loop = snap.loops[loopName];
        if (!loop) return { snapshot: snap, events: [] };
        const marked = [...(loop.markedAsPreliminary || [])];
        while (marked.length < loop.states.length) marked.push(false);
        marked[loop.index - 1] = !marked[loop.index - 1];
        const newLoop = { ...loop, markedAsPreliminary: marked };
        this._snapshot = {
            ...snap,
            loops: { ...snap.loops, [loopName]: newLoop },
        };
        return {
            snapshot: this._snapshot,
            events: [{ type: EVENT_LOOP_UPDATED, loopName, loop: newLoop }],
        };
    }

    setLoopIteration(loopName, index, options = {}) {
        const snap = this._snapshot;
        const events = [];
        const { goStartLoop = true } = options;
        let loops = { ...snap.loops };
        const loop = { ...loops[loopName], index };
        loops[loopName] = loop;
        for (const name of Object.keys(loops)) {
            if (name !== loopName && name.startsWith(loopName)) {
                loops[name] = { ...loops[name], index: 1 };
            }
        }
        let itemId = goStartLoop ? loop.firstItemId : snap.currentItemId;
        let itemsStates = { ...snap.itemsStates };
        if (loop.states[loop.index - 1] && loop.states[loop.index - 1][itemId] !== undefined) {
            itemsStates[itemId] = loop.states[loop.index - 1][itemId];
        }
        this._items[itemId].init(itemsStates, snap.globals, loops);
        while (this._items[itemId].isDecisionNode) {
            const next = this._items[itemId].chooseNext(itemsStates, snap.globals, loops);
            const nextObj = typeof next === 'string' ? this._items[next] : next;
            itemId = nextObj.id;
            nextObj.init(itemsStates, snap.globals, loops);
            if (loop.states[loop.index - 1] && loop.states[loop.index - 1][itemId] !== undefined) {
                itemsStates = { ...itemsStates, [itemId]: loop.states[loop.index - 1][itemId] };
            }
        }
        events.push({ type: EVENT_LOOP_UPDATED, loopName, loop });
        this._snapshot = {
            ...snap,
            currentItemId: itemId,
            itemsStates,
            loops,
            hasIncompleteLoop: false,
        };
        return { snapshot: this._snapshot, events };
    }

    jumpTo({ itemId, loop: pathLoop, path }) {
        const snap = this._snapshot;
        const events = [];
        let { loops, globals, itemsStates } = snap;
        const loopName = pathLoop?.loopName;
        const loopIndex = pathLoop?.loopIndex;

        if (loopName && loops[loopName]) {
            this.flowRegistry.restoreVariables(snap.currentFlowId, loopName, globals);
            loops = { ...loops, [loopName]: { ...loops[loopName], index: loopIndex } };
            const iter = loops[loopName].states[loopIndex - 1];
            itemsStates = { ...itemsStates, [itemId]: iter[itemId] };
        }

        this._items[itemId].init(itemsStates, globals, loops);
        const newPath = path || snap.path;
        events.push({ type: EVENT_ITEM_CHANGED, itemId, path: newPath });

        this._snapshot = {
            ...snap,
            currentItemId: itemId,
            itemsStates,
            loops,
            globals,
            path: newPath,
            canFreelyNavigate: true,
            hasErrors: false,
        };
        return { snapshot: this._snapshot, events };
    }

    reset() {
        return this.changeFlow(this._snapshot.currentFlowId);
    }

    hydrate(state) {
        // Replace snapshot wholesale, then rebuild derived caches by re-entering
        // the target item. Callers are expected to pass a fully-formed snapshot shape.
        const events = [];
        const prevFlowId = this._snapshot.currentFlowId;
        const flow = this.flowRegistry.get(state.currentFlowId);
        if (!flow) {
            throw new Error(`FlowEngine.hydrate: flow '${state.currentFlowId}' is not registered`);
        }
        if (prevFlowId !== state.currentFlowId) {
            this._items = {};
            flow.items.forEach((item) => { this._items[item.id] = item; });
        }
        this._snapshot = {
            currentFlowId: state.currentFlowId,
            currentFlowVersion: flow.version,
            currentFlowName: flow.name,
            currentItemId: state.currentItemId,
            itemsStates: state.itemsStates || {},
            loops: state.loops || {},
            globals: state.globals || {},
            path: state.path || [],
            hasErrors: !!state.hasErrors,
            hasIncompleteLoop: !!state.hasIncompleteLoop,
            canFreelyNavigate: state.canFreelyNavigate !== false,
        };
        this.flowRegistry.rebuildGroups(
            this._snapshot.currentFlowId,
            this._snapshot.itemsStates,
            this._snapshot.globals,
            this._snapshot.loops,
        );
        const item = this._items[this._snapshot.currentItemId];
        if (!item) {
            throw new Error(`FlowEngine.hydrate: item '${this._snapshot.currentItemId}' not found in flow '${this._snapshot.currentFlowId}'`);
        }
        item.init(this._snapshot.itemsStates, this._snapshot.globals, this._snapshot.loops);
        if (prevFlowId !== this._snapshot.currentFlowId) {
            events.push({ type: EVENT_FLOW_CHANGED, flowId: this._snapshot.currentFlowId });
        }
        events.push({ type: EVENT_PATH_UPDATED, path: this._snapshot.path });
        events.push({ type: EVENT_ITEM_CHANGED, itemId: this._snapshot.currentItemId, path: this._snapshot.path });
        return { snapshot: this.getSnapshot(), events };
    }

    validate() {
        const snap = this._snapshot;
        const item = this.getCurrentItem();
        item.validate(snap.itemsStates, snap.globals, snap.loops);
        const hasErrors = (item.errors || []).length > 0;
        this._snapshot = { ...snap, hasErrors };
        return {
            snapshot: this._snapshot,
            events: [{ type: EVENT_VALIDATION_CHANGED, hasErrors }],
        };
    }
}
